import { deleteCookie, getCookie, setCookie } from "cookies-next";

import { TEAMS_COOKIE_KEYS, TeamType } from "./props";

const sessionMaxAgeSeconds = 60 * 60 * 24 * 7;
export const TEAMS_STORAGE_EVENT = "app-sync:teams-storage-change";

export type TeamsStorageEventDetail = {
  currentTeam?: TeamType | null;
  teams?: TeamType[];
};

export function readStoredTeams(): TeamType[] {
  const localTeams = readTeamsFromRaw(
    getLocalStorageItem(TEAMS_COOKIE_KEYS.teams),
  );
  if (localTeams) {
    return localTeams;
  }

  return readTeamsFromRaw(getCookie(TEAMS_COOKIE_KEYS.teams)) ?? [];
}

export function persistStoredTeams(teams: unknown[]) {
  if (!Array.isArray(teams) || !isBrowser()) {
    return;
  }

  const normalizedTeams = normalizeTeams(teams);
  window.localStorage.setItem(
    TEAMS_COOKIE_KEYS.teams,
    JSON.stringify(normalizedTeams),
  );
  deleteCookie(TEAMS_COOKIE_KEYS.teams, { path: "/" });
  notifyTeamsStorageChange({ teams: normalizedTeams });
}

export function clearStoredTeams() {
  if (isBrowser()) {
    window.localStorage.removeItem(TEAMS_COOKIE_KEYS.teams);
  }

  deleteCookie(TEAMS_COOKIE_KEYS.teams, { path: "/" });
  notifyTeamsStorageChange({ teams: [] });
}

export function persistCurrentTeam(team: TeamType) {
  setCookie(TEAMS_COOKIE_KEYS.currentTeam, JSON.stringify(team), {
    maxAge: sessionMaxAgeSeconds,
    path: "/",
    sameSite: "lax",
  });
  notifyTeamsStorageChange({ currentTeam: team });
}

export function clearCurrentTeam() {
  deleteCookie(TEAMS_COOKIE_KEYS.currentTeam, { path: "/" });
  notifyTeamsStorageChange({ currentTeam: null });
}

function readTeamsFromRaw(value: unknown): TeamType[] | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return null;
    }

    return normalizeTeams(parsed);
  } catch {
    return null;
  }
}

export function normalizeTeams(teams: unknown[]): TeamType[] {
  return teams.filter(
    (team): team is TeamType =>
      Boolean(team) &&
      typeof (team as TeamType).value === "string" &&
      typeof (team as TeamType).name === "string",
  );
}

function getLocalStorageItem(key: string) {
  if (!isBrowser()) {
    return null;
  }

  return window.localStorage.getItem(key);
}

function isBrowser() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function notifyTeamsStorageChange(detail: TeamsStorageEventDetail) {
  if (!isBrowser() || typeof window.dispatchEvent !== "function") {
    return;
  }

  window.dispatchEvent(new CustomEvent(TEAMS_STORAGE_EVENT, { detail }));
}
